import React, { useEffect, useMemo, useState } from 'react'
import { Table, Button, Badge, ButtonGroup, Alert, Input, FormGroup, Label } from 'reactstrap'
import { NetworkStatus, useMutation, useQuery } from '@apollo/client'
import { GET_ADMIN_INBOX_GROUP_ACCESSES, GET_ADMIN_INBOX_MEMBERS, GET_ADMIN_INBOXES } from './Queries'
import { REMOVE_INBOX_GROUP_ACCESS, REMOVE_INBOX_MEMBER } from './Mutations'
import AddEditInboxMemberModal from './AddEditInboxMemberModal'

const ROLE_COLORS = {
    admin: 'danger',
    member: 'primary',
}

const ROLE_LABELS = {
    admin: 'Admin',
    member: 'Member',
}

const PAGE_SIZE = 100

const mergeConnectionResults = (previousResult, fetchMoreResult, fieldName) => {
    if (!fetchMoreResult?.[fieldName]) {
        return previousResult
    }

    const previousEdges = previousResult?.[fieldName]?.edges || []
    const nextEdges = fetchMoreResult[fieldName].edges || []
    const seenIds = new Set(previousEdges.map(edge => edge?.node?.id))
    const mergedEdges = [
        ...previousEdges,
        ...nextEdges.filter(edge => {
            const nodeId = edge?.node?.id
            if (!nodeId || seenIds.has(nodeId)) {
                return false
            }
            seenIds.add(nodeId)
            return true
        }),
    ]

    return {
        ...previousResult,
        [fieldName]: {
            ...fetchMoreResult[fieldName],
            edges: mergedEdges,
        },
    }
}

export default function InboxMemberManagement() {
    const [showModal, setShowModal] = useState(false)
    const [selectedMember, setSelectedMember] = useState(null)
    const [selectedGroupAccess, setSelectedGroupAccess] = useState(null)
    const [filterInboxId, setFilterInboxId] = useState('')
    const [searchTerm, setSearchTerm] = useState('')
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
    const [feedback, setFeedback] = useState(null)

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm.trim())
        }, 250)

        return () => clearTimeout(timeoutId)
    }, [searchTerm])

    const queryVariables = useMemo(() => ({
        first: PAGE_SIZE,
        inbox: filterInboxId || undefined,
        searchTerm: debouncedSearchTerm || undefined,
    }), [debouncedSearchTerm, filterInboxId])

    const { data: inboxesData } = useQuery(GET_ADMIN_INBOXES, {
        variables: { isArchived: false },
    })

    const {
        data: membersData,
        loading: membersLoading,
        error: membersError,
        refetch: refetchMembers,
        fetchMore: fetchMoreMembers,
        networkStatus: memberNetworkStatus,
    } = useQuery(GET_ADMIN_INBOX_MEMBERS, {
        variables: queryVariables,
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
    })

    const {
        data: groupAccessData,
        loading: groupAccessLoading,
        error: groupAccessError,
        refetch: refetchGroupAccesses,
        fetchMore: fetchMoreGroupAccesses,
        networkStatus: groupAccessNetworkStatus,
    } = useQuery(GET_ADMIN_INBOX_GROUP_ACCESSES, {
        variables: queryVariables,
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
    })

    const [removeInboxMember] = useMutation(REMOVE_INBOX_MEMBER)
    const [removeInboxGroupAccess] = useMutation(REMOVE_INBOX_GROUP_ACCESS)

    const members = membersData?.allInboxMembers?.edges?.map(edge => edge.node) || []
    const membersPageInfo = membersData?.allInboxMembers?.pageInfo
    const groupAccesses = groupAccessData?.allInboxGroupAccesses?.edges?.map(edge => edge.node) || []
    const groupAccessPageInfo = groupAccessData?.allInboxGroupAccesses?.pageInfo
    const inboxes = inboxesData?.myAdminInboxes?.edges?.map(edge => edge.node) || []
    const isLoadingMoreMembers = memberNetworkStatus === NetworkStatus.fetchMore
    const isLoadingMoreGroupAccesses = groupAccessNetworkStatus === NetworkStatus.fetchMore
    const isSearching = (
        memberNetworkStatus === NetworkStatus.setVariables
        || groupAccessNetworkStatus === NetworkStatus.setVariables
    ) && (!!membersData || !!groupAccessData)

    const handleLoadMoreMembers = () => {
        if (!membersPageInfo?.hasNextPage || !membersPageInfo?.endCursor || isLoadingMoreMembers) {
            return
        }

        fetchMoreMembers({
            variables: {
                ...queryVariables,
                after: membersPageInfo.endCursor,
            },
            updateQuery: (previousResult, { fetchMoreResult }) => (
                mergeConnectionResults(previousResult, fetchMoreResult, 'allInboxMembers')
            ),
        })
    }

    const handleLoadMoreGroupAccesses = () => {
        if (!groupAccessPageInfo?.hasNextPage || !groupAccessPageInfo?.endCursor || isLoadingMoreGroupAccesses) {
            return
        }

        fetchMoreGroupAccesses({
            variables: {
                ...queryVariables,
                after: groupAccessPageInfo.endCursor,
            },
            updateQuery: (previousResult, { fetchMoreResult }) => (
                mergeConnectionResults(previousResult, fetchMoreResult, 'allInboxGroupAccesses')
            ),
        })
    }

    const closeModal = () => {
        setShowModal(false)
        setSelectedMember(null)
        setSelectedGroupAccess(null)
    }

    const handleAdd = () => {
        setSelectedMember(null)
        setSelectedGroupAccess(null)
        setShowModal(true)
    }

    const handleEditMember = (member) => {
        setSelectedMember(member)
        setSelectedGroupAccess(null)
        setShowModal(true)
    }

    const handleEditGroupAccess = (groupAccess) => {
        setSelectedMember(null)
        setSelectedGroupAccess(groupAccess)
        setShowModal(true)
    }

    const refreshLists = () => {
        refetchMembers(queryVariables)
        refetchGroupAccesses(queryVariables)
    }

    const handleRemoveMember = (member) => {
        const userName = member.user?.username || 'this user'
        const inboxName = member.inbox?.name || 'the inbox'
        if (window.confirm(`Are you sure you want to remove ${userName} from ${inboxName}?`)) {
            removeInboxMember({
                variables: {
                    id: member.id,
                },
            })
                .then(({ data: mutationData }) => {
                    const response = mutationData?.messagingRemoveInboxMember
                    if (response?.ok) {
                        refreshLists()
                        setFeedback({
                            type: 'success',
                            message: `${userName} has been removed from ${inboxName}.`,
                        })
                    } else {
                        setFeedback({
                            type: 'danger',
                            message: response?.errors?.join(', ') || 'Failed to remove member.',
                        })
                    }
                })
                .catch((mutationError) => {
                    setFeedback({
                        type: 'danger',
                        message: mutationError.message || 'Unexpected error removing member.',
                    })
                })
        }
    }

    const handleRemoveGroupAccess = (groupAccess) => {
        const groupName = groupAccess.group?.name || 'this group'
        const inboxName = groupAccess.inbox?.name || 'the inbox'
        if (window.confirm(`Are you sure you want to remove ${groupName}'s access to ${inboxName}?`)) {
            removeInboxGroupAccess({
                variables: {
                    id: groupAccess.id,
                },
            })
                .then(({ data: mutationData }) => {
                    const response = mutationData?.messagingRemoveInboxGroupAccess
                    if (response?.ok) {
                        refreshLists()
                        setFeedback({
                            type: 'success',
                            message: `${groupName} no longer has access to ${inboxName}.`,
                        })
                    } else {
                        setFeedback({
                            type: 'danger',
                            message: response?.errors?.join(', ') || 'Failed to remove group access.',
                        })
                    }
                })
                .catch((mutationError) => {
                    setFeedback({
                        type: 'danger',
                        message: mutationError.message || 'Unexpected error removing group access.',
                    })
                })
        }
    }

    const handleModalSuccess = (message) => {
        refreshLists()
        if (message) {
            setFeedback({
                type: 'success',
                message,
            })
        }
    }

    if ((membersLoading && !membersData) || (groupAccessLoading && !groupAccessData)) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border" role="status">
                    <span className="sr-only">Loading...</span>
                </div>
            </div>
        )
    }

    if (membersError || groupAccessError) {
        return (
            <Alert color="danger">
                Error loading inbox access: {membersError?.message || groupAccessError?.message}
            </Alert>
        )
    }

    const showDirectMembersEmptyState = members.length === 0
    const showGroupAccessEmptyState = groupAccesses.length === 0
    const showCompletelyEmptyState = showDirectMembersEmptyState && showGroupAccessEmptyState

    return (
        <div className="comms-admin-section">
            <div className="comms-admin-section-header">
                <div>
                    <h3>Inbox Access</h3>
                    <p className="text-muted">Manage direct members and group-based inbox access</p>
                </div>
                <div>
                    <Button color="primary" onClick={handleAdd}>
                        <i className="fa fa-plus"></i> Add Access
                    </Button>
                </div>
            </div>

            {feedback && (
                <Alert color={feedback.type}>
                    <i className="fa fa-close" onClick={() => setFeedback(null)}></i> {feedback.message}
                </Alert>
            )}

            <div className="comms-admin-filters mb-3">
                <FormGroup className="mb-0">
                    <Label for="inboxFilter" className="mr-2">Filter by Inbox:</Label>
                    <Input
                        type="select"
                        id="inboxFilter"
                        value={filterInboxId}
                        onChange={(e) => setFilterInboxId(e.target.value)}
                        style={{ width: '250px', display: 'inline-block' }}
                    >
                        <option value="">All Inboxes</option>
                        {inboxes.map((inbox) => (
                            <option key={inbox.id} value={inbox.id}>
                                {inbox.name}
                            </option>
                        ))}
                    </Input>
                </FormGroup>
                <FormGroup className="mb-0 flex-grow-1">
                    <Input
                        type="search"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search direct members by name/email or groups by name…"
                    />
                </FormGroup>
            </div>

            {isSearching && (
                <div className="text-muted small mb-3">
                    Searching inbox access…
                </div>
            )}

            {showCompletelyEmptyState ? (
                <Alert color="info">
                    {debouncedSearchTerm
                        ? 'No direct members or group access records match your search.'
                        : 'No inbox access found yet. Add users or groups to get started.'}
                </Alert>
            ) : (
                <>
                    <div className="mb-4">
                        <div className="d-flex align-items-center justify-content-between mb-2">
                            <h4 className="mb-0">Direct Members</h4>
                        </div>

                        {showDirectMembersEmptyState ? (
                            <Alert color="light" className="mb-0">
                                {debouncedSearchTerm
                                    ? 'No direct members match this search.'
                                    : 'No direct members found for the selected inbox filter.'}
                            </Alert>
                        ) : (
                            <>
                                <Table responsive striped hover className="comms-admin-table">
                                    <thead>
                                        <tr>
                                            <th>User</th>
                                            <th>Email</th>
                                            <th>Inbox</th>
                                            <th>Role</th>
                                            <th>Can Be Assigned</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {members.map((member) => (
                                            <tr key={member.id}>
                                                <td>
                                                    <strong>{member.user?.username || 'N/A'}</strong>
                                                    {member.user?.firstName && member.user?.lastName && (
                                                        <div className="text-muted small">
                                                            {member.user.firstName} {member.user.lastName}
                                                        </div>
                                                    )}
                                                </td>
                                                <td>{member.user?.email || 'N/A'}</td>
                                                <td>{member.inbox?.name || 'N/A'}</td>
                                                <td>
                                                    <Badge color={ROLE_COLORS[member.role] || 'secondary'}>
                                                        {ROLE_LABELS[member.role] || member.role}
                                                    </Badge>
                                                </td>
                                                <td>
                                                    {member.canBeAssigned ? (
                                                        <Badge color="success">Yes</Badge>
                                                    ) : (
                                                        <Badge color="secondary">No</Badge>
                                                    )}
                                                </td>
                                                <td>
                                                    <ButtonGroup size="sm">
                                                        <Button
                                                            color="info"
                                                            outline
                                                            onClick={() => handleEditMember(member)}
                                                            title="Edit Direct Member"
                                                        >
                                                            <i className="fa fa-edit"></i>
                                                        </Button>
                                                        <Button
                                                            color="danger"
                                                            outline
                                                            onClick={() => handleRemoveMember(member)}
                                                            title="Remove Direct Member"
                                                        >
                                                            <i className="fa fa-trash"></i>
                                                        </Button>
                                                    </ButtonGroup>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>

                                {membersPageInfo?.hasNextPage && (
                                    <div className="text-center mt-3">
                                        <Button
                                            color="secondary"
                                            outline
                                            onClick={handleLoadMoreMembers}
                                            disabled={isLoadingMoreMembers}
                                        >
                                            {isLoadingMoreMembers ? 'Loading…' : 'Load more direct members'}
                                        </Button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    <div>
                        <div className="d-flex align-items-center justify-content-between mb-2">
                            <h4 className="mb-0">Group Access</h4>
                        </div>

                        {showGroupAccessEmptyState ? (
                            <Alert color="light" className="mb-0">
                                {debouncedSearchTerm
                                    ? 'No group access records match this search.'
                                    : 'No inbox groups found for the selected inbox filter.'}
                            </Alert>
                        ) : (
                            <>
                                <Table responsive striped hover className="comms-admin-table">
                                    <thead>
                                        <tr>
                                            <th>Group</th>
                                            <th>Inbox</th>
                                            <th>Role</th>
                                            <th>Can Be Assigned</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {groupAccesses.map((groupAccess) => (
                                            <tr key={groupAccess.id}>
                                                <td>
                                                    <strong>{groupAccess.group?.name || 'N/A'}</strong>
                                                </td>
                                                <td>{groupAccess.inbox?.name || 'N/A'}</td>
                                                <td>
                                                    <Badge color={ROLE_COLORS[groupAccess.role] || 'secondary'}>
                                                        {ROLE_LABELS[groupAccess.role] || groupAccess.role}
                                                    </Badge>
                                                </td>
                                                <td>
                                                    {groupAccess.canBeAssigned ? (
                                                        <Badge color="success">Yes</Badge>
                                                    ) : (
                                                        <Badge color="secondary">No</Badge>
                                                    )}
                                                </td>
                                                <td>
                                                    <ButtonGroup size="sm">
                                                        <Button
                                                            color="info"
                                                            outline
                                                            onClick={() => handleEditGroupAccess(groupAccess)}
                                                            title="Edit Group Access"
                                                        >
                                                            <i className="fa fa-edit"></i>
                                                        </Button>
                                                        <Button
                                                            color="danger"
                                                            outline
                                                            onClick={() => handleRemoveGroupAccess(groupAccess)}
                                                            title="Remove Group Access"
                                                        >
                                                            <i className="fa fa-trash"></i>
                                                        </Button>
                                                    </ButtonGroup>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>

                                {groupAccessPageInfo?.hasNextPage && (
                                    <div className="text-center mt-3">
                                        <Button
                                            color="secondary"
                                            outline
                                            onClick={handleLoadMoreGroupAccesses}
                                            disabled={isLoadingMoreGroupAccesses}
                                        >
                                            {isLoadingMoreGroupAccesses ? 'Loading…' : 'Load more group access'}
                                        </Button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </>
            )}

            <AddEditInboxMemberModal
                isOpen={showModal}
                toggle={closeModal}
                inboxMember={selectedMember}
                inboxGroupAccess={selectedGroupAccess}
                onSuccess={handleModalSuccess}
            />
        </div>
    )
}
